import {
  getFloorUnitsGeoJson,
  getFloorBasemap,
  getProjectParcelsGeoJsonStatic,
} from "./api";
import spacesService from "./spacesService";
import { SPACES_CONFIG } from "../config/constants";
import MapLogger from "../utils/MapLogger";
import MapErrorHandler from "../utils/MapErrorHandler";

/**
 * Map Project Service
 * Handles display of residential and land projects on the map separately
 */
class MapProjectService {
  /**
   * Calculate distance between two points using Haversine formula
   * @param {number} lat1 - First point latitude
   * @param {number} lon1 - First point longitude
   * @param {number} lat2 - Second point latitude
   * @param {number} lon2 - Second point longitude
   * @returns {number} - Distance in meters
   */
  static calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371e3; // Earth's radius in meters
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  /**
   * Calculate parcel dimensions from polygon coordinates
   * @param {Array|Object} coordinates - Polygon coordinates
   * @returns {Object} - Dimensions and center point
   */
  static calculateParcelDimensions(coordinates) {
    // Handle different coordinate formats
    let ring = null;

    if (!coordinates) {
      console.error("No coordinates provided");
      return { dimensions: [], center: null };
    }

    // Case 1: Single coordinate pair [lng, lat] - not a polygon
    if (
      Array.isArray(coordinates) &&
      coordinates.length === 2 &&
      typeof coordinates[0] === "number" &&
      typeof coordinates[1] === "number"
    ) {
      return { dimensions: [], center: null };
    }

    // Case 2: coordinates is already a ring (array of coordinate pairs)
    if (
      Array.isArray(coordinates) &&
      coordinates.length > 0 &&
      Array.isArray(coordinates[0])
    ) {
      // Check if coordinates[0] is a coordinate pair [lng, lat] or another array
      if (
        coordinates[0].length === 2 &&
        typeof coordinates[0][0] === "number"
      ) {
        ring = coordinates;
      } else if (Array.isArray(coordinates[0]) && coordinates[0].length > 0) {
        // coordinates[0] is actually the ring
        ring = coordinates[0];
      }
    }
    // Case 3: coordinates[0] is the ring (GeoJSON Polygon format)
    else if (
      Array.isArray(coordinates) &&
      Array.isArray(coordinates[0]) &&
      coordinates[0].length > 0 &&
      Array.isArray(coordinates[0][0])
    ) {
      ring = coordinates[0];
    }
    // Case 4: coordinates.coordinates exists (nested GeoJSON)
    else if (
      coordinates.coordinates &&
      Array.isArray(coordinates.coordinates)
    ) {
      if (
        Array.isArray(coordinates.coordinates[0]) &&
        Array.isArray(coordinates.coordinates[0][0])
      ) {
        ring = coordinates.coordinates[0];
      } else if (Array.isArray(coordinates.coordinates[0])) {
        ring = coordinates.coordinates;
      }
    }

    // Validate the ring
    if (!ring || !Array.isArray(ring)) {
      console.error(
        "Could not extract valid ring from coordinates:",
        coordinates
      );
      return { dimensions: [], center: null };
    }

    if (ring.length < 3) {
      console.error("Ring has less than 3 points:", ring.length);
      return { dimensions: [], center: null };
    }

    if (!Array.isArray(ring[0])) {
      console.error("Ring[0] is not an array:", ring[0]);
      return { dimensions: [], center: null };
    }

    if (ring[0].length < 2) {
      console.error("Ring[0] does not have at least 2 coordinates:", ring[0]);
      return { dimensions: [], center: null };
    }

    const dimensions = [];
    const center = {
      lng: ring.reduce((sum, coord) => sum + coord[0], 0) / ring.length,
      lat: ring.reduce((sum, coord) => sum + coord[1], 0) / ring.length,
    };

    // Calculate distance for each side of the polygon
    for (let i = 0; i < ring.length - 1; i++) {
      const p1 = ring[i];
      const p2 = ring[i + 1];
      const distance = this.calculateDistance(p1[1], p1[0], p2[1], p2[0]);

      // Calculate midpoint of the side
      const midpoint = {
        lng: (p1[0] + p2[0]) / 2,
        lat: (p1[1] + p2[1]) / 2,
      };

      // Calculate angle for text rotation
      let angle = (Math.atan2(p2[1] - p1[1], p2[0] - p1[0]) * 180) / Math.PI;
      if (angle > 90 || angle < -90) {
        angle += 180;
      }
      dimensions.push({
        distance: distance,
        midpoint: midpoint,
        angle: angle,
        start: { lng: p1[0], lat: p1[1] },
        end: { lng: p2[0], lat: p2[1] },
      });
    }

    return { dimensions, center };
  }

  /**
   * Show parcel dimensions on the map
   * @param {Object} mapRef - Map reference
   * @param {Object} parcelData - Parcel data with coordinates
   */
  static showParcelDimensions(mapRef, parcelData) {
    if (!mapRef || !mapRef.current || !parcelData.coordinates) {
      MapLogger.logWarning(
        "dimensions",
        "Cannot show dimensions - missing map reference or coordinates",
        {
          hasMapRef: !!mapRef,
          hasMap: !!(mapRef && mapRef.current),
          hasCoordinates: !!parcelData.coordinates,
          parcelNumber: parcelData.parcel_number,
        }
      );
      return;
    }

    try {
      // Parse coordinates based on different possible formats
      let coordinates;

      if (typeof parcelData.coordinates === "string") {
        // Parse JSON string
        try {
          const parsed = JSON.parse(parcelData.coordinates);
          coordinates = parsed.coordinates || parsed;
        } catch (parseError) {
          console.error("Failed to parse coordinates string:", parseError);
          return;
        }
      } else if (
        parcelData.coordinates &&
        typeof parcelData.coordinates === "object"
      ) {
        if (parcelData.coordinates.coordinates) {
          // GeoJSON format
          coordinates = parcelData.coordinates.coordinates;
        } else if (Array.isArray(parcelData.coordinates)) {
          // Direct coordinates array
          coordinates = parcelData.coordinates;
        } else {
          // Try to use the object directly
          coordinates = parcelData.coordinates;
        }
      } else {
        console.error(
          "Unknown coordinates format:",
          typeof parcelData.coordinates,
          parcelData.coordinates
        );
        return;
      }

      // Additional validation
      if (!coordinates) {
        console.error("No valid coordinates found after parsing");
        return;
      }

      const { dimensions, center } =
        this.calculateParcelDimensions(coordinates);

      if (dimensions.length === 0) {
        MapLogger.logWarning(
          "dimensions",
          "No dimensions calculated for parcel",
          {
            parcelNumber: parcelData.parcel_number,
          }
        );
        return;
      }

      // Remove existing dimensions layers if they exist
      this.hideParcelDimensions(mapRef);

      // Create dimension lines and labels
      const dimensionFeatures = [];
      const lineFeatures = [];

      dimensions.forEach((dim, index) => {
        // Create dimension line
        lineFeatures.push({
          type: "Feature",
          geometry: {
            type: "LineString",
            coordinates: [
              [dim.start.lng, dim.start.lat],
              [dim.end.lng, dim.end.lat],
            ],
          },
          properties: {
            type: "dimension-line",
          },
        });

        // Create dimension label
        const distanceText =
          dim.distance < 1000
            ? `${Math.round(dim.distance * 100) / 100}م`
            : `${Math.round(dim.distance / 10) / 100}كم`;

        dimensionFeatures.push({
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: [dim.midpoint.lng, dim.midpoint.lat],
          },
          properties: {
            distance: distanceText,
            angle: dim.angle,
            type: "dimension-label",
          },
        });
      });

      // Add dimension lines source and layer
      mapRef.current.addSource("parcel-dimension-lines", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: lineFeatures,
        },
      });

      mapRef.current.addLayer({
        id: "parcel-dimension-lines",
        type: "line",
        source: "parcel-dimension-lines",
        paint: {
          "line-color": "#ff6b35",
          "line-width": [
            "interpolate",
            ["linear"],
            ["zoom"],
            14,
            2,
            18,
            4,
            22,
            6,
          ],
          "line-dasharray": [3, 3],
        },
        minzoom: 14,
      });

      // Add dimensions source and layer
      mapRef.current.addSource("parcel-dimensions", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: dimensionFeatures,
        },
      });

      mapRef.current.addLayer({
        id: "parcel-dimensions",
        type: "symbol",
        source: "parcel-dimensions",
        layout: {
          "text-field": ["get", "distance"],
          "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
          "text-size": [
            "interpolate",
            ["linear"],
            ["zoom"],
            14,
            10,
            18,
            14,
            22,
            18,
          ],
          "text-rotate": 0,
          "text-anchor": "center",
          "text-allow-overlap": true,
          "text-ignore-placement": true,
        },
        paint: {
          "text-color": "#ffffff",
          "text-halo-color": "#ff6b35",
          "text-halo-width": 3,
        },
        minzoom: 14,
      });

      MapLogger.logLayerOperation("dimensions", "Added successfully", {
        parcelNumber: parcelData.parcel_number,
        dimensionsCount: dimensions.length,
      });
    } catch (error) {
      MapErrorHandler.handleLayerError(error, "dimensions");
      console.error("Error showing unit dimensions:", error);
    }
  }

  /**
   * Hide parcel dimensions from the map
   * @param {Object} mapRef - Map reference
   */
  static hideParcelDimensions(mapRef) {
    if (!mapRef || !mapRef.current) {
      return;
    }

    // Check if map has required methods (map might be in process of being destroyed)
    if (
      typeof mapRef.current.getLayer !== "function" ||
      typeof mapRef.current.getSource !== "function" ||
      typeof mapRef.current.removeLayer !== "function" ||
      typeof mapRef.current.removeSource !== "function"
    ) {
      return;
    }

    const map = mapRef.current;
    const safeRemoveLayer = (id) => {
      try {
        if (map.getLayer(id)) map.removeLayer(id);
      } catch (e) {
        console.warn(`Failed to remove layer ${id}:`, e);
      }
    };
    const safeRemoveSource = (id) => {
      try {
        if (map.getSource(id)) map.removeSource(id);
      } catch (e) {
        console.warn(`Failed to remove source ${id}:`, e);
      }
    };

    safeRemoveLayer("parcel-dimensions");
    safeRemoveLayer("parcel-dimension-lines");
    safeRemoveSource("parcel-dimensions");
    safeRemoveSource("parcel-dimension-lines");

    MapLogger.logLayerOperation("dimensions", "Hidden successfully");
  }

  /**
   * Load and display residential project with specified floors
   * @param {Object} mapRef - Map reference
   * @param {number} projectId - Project ID
   * @param {Array} floorFilters - Array of selected floor IDs
   * @param {Object} project - Project data
   * @param {Function} onParcelClick - Callback function for parcel click events
   * @returns {Promise<Object>} - Load result
   */
  static async loadResidentialProject(
    mapRef,
    projectId,
    floorFilters,
    project,
    onParcelClick = null
  ) {
    try {
      MapLogger.logProjectDisplay(projectId, "residential", "Starting load");

      // console.log("####floorData start", projectId, floorFilters);

      if (!mapRef || !mapRef.current) {
        throw new Error("Map reference is not available");
      }

      if (!floorFilters || floorFilters.length === 0) {
        MapLogger.logProjectDisplay(
          projectId,
          "residential",
          "No floor filters provided"
        );
        return { success: false, message: "No floor filters provided" };
      }

      const parcelData = await this.loadParcelData(projectId, project);

      if (!parcelData.success) {
        return parcelData;
      }

      mapRef.current.off("click", "parcels-fill");

      if (onParcelClick && typeof onParcelClick === "function") {
        mapRef.current.on("click", "parcels-fill", async (e) => {
          const feature = e.features[0];
          const parcelData = feature.properties;
          const parcelGeometry = feature.geometry;

          // Create complete parcel data with geometry for dimensions
          const parcelDataWithCoords = {
            ...parcelData,
            coordinates: parcelGeometry,
          };
          const coords = parcelGeometry.coordinates[0];
          let totalLng = 0,
            totalLat = 0,
            count = 0;
          let minLat = Infinity; // For finding the bottom edge
          coords.forEach((coord) => {
            if (
              Array.isArray(coord) &&
              coord.length >= 2 &&
              typeof coord[0] === "number" &&
              typeof coord[1] === "number"
            ) {
              totalLng += coord[0];
              totalLat += coord[1];
              count++;

              // Track minimum latitude (southernmost point)
              if (coord[1] < minLat) {
                minLat = coord[1];
              }
            }
          });
          var center = [totalLng / count, minLat];
          var zoom = 20;
          await mapRef.current.flyTo({ center: center, zoom: zoom });

          // Dimensions are drawn by the parcel-click handler after the
          // camera settles (moveend), so they line up with the final
          // geometry rather than being stamped mid-flyTo.
          onParcelClick(parcelDataWithCoords);

          if (mapRef.current.getLayer("parcels-outline")) {
            mapRef.current.setPaintProperty("parcels-outline", "line-color", [
              "case",
              ["==", ["get", "id"], parcelData.id || -1],
              "#ffffff", // White border for selected parcel
              "#2980b9", // Default blue
            ]);
            mapRef.current.setPaintProperty("parcels-outline", "line-width", [
              "case",
              ["==", ["get", "id"], parcelData.id || -1],
              4, // Thicker white line for selected parcel
              1, // Default thickness
            ]);
            // ... width updates
          }
        });
      }

      // Clear any lingering dimension overlay from the previously selected unit on the old floor
      this.hideParcelDimensions(mapRef);

      await this.addFloorBasemapProject(mapRef, project, floorFilters[0]);
      // Add parcel layers
      // console.log("start to load floor ID ", floorFilters[0]);
      await this.addParcelLayersFloors(
        mapRef,
        parcelData.data,
        floorFilters[0]
      );

      if (true) {
        // return;
        return { success: true, data: null };
      }

      // Clear existing layers
      await this.clearExistingLayers(mapRef);

      // Hide any existing dimensions
      this.hideParcelDimensions(mapRef);

      // Load floor data
      const floorData = await this.loadFloorData(projectId, floorFilters);

      MapLogger.logMapInfo("Floor data loaded", {
        projectId,
        floorFilters,
        success: floorData.success,
        hasData: !!floorData.data,
        error: floorData.error,
        message: floorData.message,
      });

      if (!floorData.success) {
        MapLogger.logWarning("floor-data", "Failed to load floor data", {
          projectId,
          floorFilters,
          error: floorData.error || floorData.message,
        });
        return floorData;
      }

      // Add floor layesrs

      // Load and add floor basemap
      // console.log("####floorData start", projectId, floorFilters);
      try {
        await this.addFloorBasemap(mapRef, floorFilters[0]);
        await this.addFloorLayers(mapRef, floorData.data, floorFilters[0]);
      } catch (error) {
        await this.clearExistingLayers(mapRef);
        //wait 1 second
        await new Promise((resolve) => setTimeout(resolve, 1000));
        await this.addFloorBasemap(mapRef, floorFilters[0]);
        await this.addFloorLayers(mapRef, floorData.data, floorFilters[0]);
        // console.error("Error adding floor basemap:", error);
      }
      // await this.addFloorBasemap(mapRef, floorFilters[0]);
      // await this.addFloorLayers(mapRef, floorData.data, floorFilters[0]);

      // Add click handler for parcel selection
      mapRef.current.off("click", "parcels-fill");

      if (onParcelClick && typeof onParcelClick === "function") {
        mapRef.current.on("click", "parcels-fill", async (e) => {
          const feature = e.features[0];
          const parcelData = feature.properties;
          const parcelGeometry = feature.geometry;

          // Create complete parcel data with geometry for dimensions
          const parcelDataWithCoords = {
            ...parcelData,
            coordinates: parcelGeometry,
          };
          const coords = parcelGeometry.coordinates[0];
          let totalLng = 0,
            totalLat = 0,
            count = 0;
          let minLat = Infinity; // For finding the bottom edge
          coords.forEach((coord) => {
            if (
              Array.isArray(coord) &&
              coord.length >= 2 &&
              typeof coord[0] === "number" &&
              typeof coord[1] === "number"
            ) {
              totalLng += coord[0];
              totalLat += coord[1];
              count++;

              // Track minimum latitude (southernmost point)
              if (coord[1] < minLat) {
                minLat = coord[1];
              }
            }
          });
          var center = [totalLng / count, minLat];
          var zoom = 20;
          await mapRef.current.flyTo({ center: center, zoom: zoom });

          // Dimensions are drawn by the parcel-click handler after the
          // camera settles (moveend), so they line up with the final
          // geometry rather than being stamped mid-flyTo.
          onParcelClick(parcelDataWithCoords);

          if (mapRef.current.getLayer("parcels-outline")) {
            mapRef.current.setPaintProperty("parcels-outline", "line-color", [
              "case",
              ["==", ["get", "id"], parcelData.id || -1],
              "#ffffff", // White border for selected parcel
              "#2980b9", // Default blue
            ]);
            mapRef.current.setPaintProperty("parcels-outline", "line-width", [
              "case",
              ["==", ["get", "id"], parcelData.id || -1],
              4, // Thicker white line for selected parcel
              1, // Default thickness
            ]);
            // ... width updates
          }
        });
      }
      MapLogger.logProjectDisplay(
        projectId,
        "residential",
        "Successfully loaded"
      );
      return { success: true, data: floorData.data };
    } catch (error) {
      MapErrorHandler.handleProjectLoadError(error, projectId);
      return { success: false, error: error.message };
    }
  }

  /**
   * Load and display land project
   * @param {Object} mapRef - Map reference
   * @param {number} projectId - Project ID
   * @param {Object} project - Project data
   * @param {Function} onParcelClick - Callback function for parcel click events
   * @returns {Promise<Object>} - Load result
   */
  static async loadLandProject(
    mapRef,
    projectId,
    project,
    onParcelClick = null
  ) {
    try {
      MapLogger.logProjectDisplay(projectId, "land", "Starting load");

      if (!mapRef || !mapRef.current) {
        throw new Error("Map reference is not available");
      }

      // Clear existing layers
      await this.clearExistingLayers(mapRef);

      // Hide any existing dimensions
      this.hideParcelDimensions(mapRef);

      // Load parcel data
      const parcelData = await this.loadParcelData(projectId, project);

      if (!parcelData.success) {
        return parcelData;
      }

      // Add parcel layers
      await this.addParcelLayers(mapRef, parcelData.data);
      mapRef.current.off("click", "parcels-fill");
      // Add click handler for parcel selection
      if (onParcelClick && typeof onParcelClick === "function") {
        mapRef.current.on("click", "parcels-fill", async (e) => {
          const feature = e.features[0];
          const parcelData = feature.properties;
          const parcelGeometry = feature.geometry;

          // Create complete parcel data with geometry for dimensions
          const parcelDataWithCoords = {
            ...parcelData,
            coordinates: parcelGeometry,
          };

          const coords = parcelGeometry.coordinates[0];
          let totalLng = 0,
            totalLat = 0,
            count = 0;
          let minLat = Infinity; // For finding the bottom edge
          coords.forEach((coord) => {
            if (
              Array.isArray(coord) &&
              coord.length >= 2 &&
              typeof coord[0] === "number" &&
              typeof coord[1] === "number"
            ) {
              totalLng += coord[0];
              totalLat += coord[1];
              count++;

              // Track minimum latitude (southernmost point)
              if (coord[1] < minLat) {
                minLat = coord[1];
              }
            }
          });
          var center = [totalLng / count, minLat];
          var zoom = 18;
          await mapRef.current.flyTo({ center: center, zoom: zoom });

          // Dimensions are drawn by the parcel-click handler after the
          // camera settles (moveend), so they line up with the final
          // geometry rather than being stamped mid-flyTo.
          onParcelClick(parcelDataWithCoords);

          if (mapRef.current.getLayer("parcels-outline")) {
            mapRef.current.setPaintProperty("parcels-outline", "line-color", [
              "case",
              ["==", ["get", "id"], parcelData.id || -1],
              "#ffffff", // White border for selected parcel
              "#2980b9", // Default blue
            ]);
            mapRef.current.setPaintProperty("parcels-outline", "line-width", [
              "case",
              ["==", ["get", "id"], parcelData.id || -1],
              4, // Thicker white line for selected parcel
              1, // Default thickness
            ]);
          }
        });
      }

      MapLogger.logProjectDisplay(projectId, "land", "Successfully loaded");
      return { success: true, data: parcelData.data };
    } catch (error) {
      MapErrorHandler.handleProjectLoadError(error, projectId);
      return { success: false, error: error.message };
    }
  }

  /**
   * Load floor data for residential projects
   * @param {number} projectId - Project ID
   * @param {Array} floorFilters - Array of floor IDs
   * @returns {Promise<Object>} - Floor data
   */
  static async loadFloorData(projectId, floorFilters) {
    try {
      const floorId = floorFilters[0]; // Take only the first floor

      // Load floor units data
      const unitsResponse = await getFloorUnitsGeoJson(floorId);

      MapLogger.logMapInfo("Floor units API response", {
        floorId,
        hasResponse: !!unitsResponse,
        hasData: !!(unitsResponse && unitsResponse.data),
        responseKeys: unitsResponse ? Object.keys(unitsResponse) : [],
      });

      // Check if response is valid
      if (!unitsResponse || !unitsResponse.data) {
        MapLogger.logError(
          "floor-data",
          "Invalid response from floor units API",
          new Error("Invalid response"),
          {
            floorId,
            unitsResponse,
          }
        );
        return {
          success: false,
          error: "Invalid response from floor units API",
        };
      }

      const unitsData = unitsResponse.data;
      // console.log('####floorData$$$$', unitsData);
      // console.log('####floorData$$$$', unitsData.features.length === 0 ,  unitsData );
      // if (!unitsData || !unitsData.features || unitsData.features.length === 0) {
      //   return {
      //     success: false,
      //     message: 'No units dd data found for the selected floor' ,unitsData
      //   };
      // }

      return { success: true, data: unitsData };
    } catch (error) {
      MapErrorHandler.handleLayerError(error, "floor-data");
      return { success: false, error: error.message };
    }
  }

  /**
   * Load parcel data for regular projects
   * @param {number} projectId - Project ID
   * @param {Object} project - Project data
   * @returns {Promise<Object>} - Parcel data
   */
  static async loadParcelData(projectId, project) {
    try {
      // Check if direct URLs can be used
      const canUseDirectUrls = spacesService.isDirectAccessAvailable();

      if (canUseDirectUrls) {
        // Use direct URLs
        const directUrl = spacesService.getDirectUrl(projectId, "compressed");
        return { success: true, data: directUrl, isDirectUrl: true };
      } else {
        // Use API
        const response = await getProjectParcelsGeoJsonStatic(projectId);
        const parcelData = response.data.data;

        if (
          !parcelData ||
          !parcelData.features ||
          parcelData.features.length === 0
        ) {
          return {
            success: false,
            message: "No parcels data found for this project",
          };
        }

        return { success: true, data: parcelData, isDirectUrl: false };
      }
    } catch (error) {
      MapErrorHandler.handleLayerError(error, "parcel-data");
      return { success: false, error: error.message };
    }
  }

  /**
   * Add floor layers to the map
   * @param {Object} mapRef - Map reference
   * @param {Object} floorData - Floor data
   * @param {number} floorId - Floor ID
   */
  static async addFloorLayers(mapRef, floorData, floorId) {
    try {
      // Add data source
      mapRef.current.addSource("parcels", {
        type: "geojson",
        data: floorData,
      });

      if (!mapRef.current.getLayer("parcels-fill")) {
        mapRef.current.addLayer({
          id: "parcels-fill",
          type: "fill",
          source: "parcels",
          paint: {
            "fill-color": [
              "case",
              ["==", ["get", "sales_status"], "Available"],
              "#10b981", // Emerald green
              ["==", ["get", "sales_status"], "Not Available"],
              "#3b82f6", // Blue
              ["==", ["get", "sales_status"], "Reserved"],
              "#f59e0b", // Amber
              ["==", ["get", "sales_status"], "Sold"],
              "#ef4444", // Red
              "#6b7280", // Gray
            ],
            "fill-opacity": 0.5,
          },
          minzoom: 14,
        });
      }

      // Add parcels outline layer
      if (!mapRef.current.getLayer("parcels-outline")) {
        mapRef.current.addLayer({
          id: "parcels-outline",
          type: "line",
          source: "parcels",
          paint: {
            "line-color": "#2980b9",
            "line-width": 1,
          },
          minzoom: 14,
        });
      }

      // Add parcels labels layer
      if (!mapRef.current.getLayer("parcels-labels")) {
        mapRef.current.addLayer({
          id: "parcels-labels",
          type: "symbol",
          source: "parcels",
          layout: {
            "text-field": ["get", "parcel_number"],
            "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],

            "text-size": 12,
          },
          paint: {
            "text-color": "#ffffff",
            "text-halo-color": "#000000",
            "text-halo-width": 0,
          },
          minzoom: 16,
        });
      }

      // Add price display layer (initially hidden)
      if (!mapRef.current.getLayer("parcels-prices")) {
        mapRef.current.addLayer({
          id: "parcels-prices",
          type: "symbol",
          source: "parcels",
          layout: {
            "text-field": [
              "format",
              ["get", "parcel_number"],
              { "font-scale": 1.4, "text-color": "#ffffff" },

              "\n",
              {},
              ["to-string", ["get", "price_with_tax"]],
              { "font-scale": 1.0, "text-color": "#626262" },
              " SAR",
              { "font-scale": 1.0, "text-color": "#626262" },
            ],
            "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
            "text-size": 12,
            // "text-weight": "bold",

            "text-offset": [0, 0],
            "text-anchor": "center",
            "text-allow-overlap": false,
            "text-ignore-placement": false,
            visibility: "none", // Initially hidden
          },
          paint: {
            "text-color": "#ffffff",
            "text-halo-color": "#000000",
            "text-halo-width": 0,
          },
          minzoom: 14,
        });
      }
      MapLogger.logLayerOperation("floor-layers", "Added successfully");
    } catch (error) {
      MapErrorHandler.handleLayerError(error, "floor-layers");
    }
  }
  /**
   * Add parcel layers to the map
   * @param {Object} mapRef - Map reference
   * @param {Object|string} parcelData - Parcel data or direct URL
   */
  static async addParcelLayersFloors(mapRef, parcelData, floorId) {
    try {
      // Add data source
      if (typeof parcelData === "string") {
        // Direct URL
        mapRef.current.addSource("parcels", {
          type: "geojson",
          data: parcelData,
        });
      } else {
        // Direct data
        mapRef.current.addSource("parcels", {
          type: "geojson",
          data: parcelData,
        });
      }

      // Add parcels fill layer
      if (!mapRef.current.getLayer("parcels-fill")) {
        mapRef.current.addLayer({
          id: "parcels-fill",
          type: "fill",
          source: "parcels",
          paint: {
            "fill-color": [
              "case",
              ["==", ["get", "sales_status"], "Available"],
              "#10b981", // Emerald green
              ["==", ["get", "sales_status"], "Not Available"],
              "#3b82f6", // Blue
              ["==", ["get", "sales_status"], "Reserved"],
              "#f59e0b", // Amber
              ["==", ["get", "sales_status"], "Sold"],
              "#ef4444", // Red
              "#6b7280", // Gray
            ],
            "fill-opacity": 0.5,
          },
          minzoom: 14,
        });
      }

      // Add parcels outline layer
      if (!mapRef.current.getLayer("parcels-outline")) {
        mapRef.current.addLayer({
          id: "parcels-outline",
          type: "line",
          source: "parcels",
          paint: {
            "line-color": "#2980b9",
            "line-width": 1,
          },
          minzoom: 14,
        });
      }

      // Add parcels labels layer
      if (!mapRef.current.getLayer("parcels-labels")) {
        mapRef.current.addLayer({
          id: "parcels-labels",
          type: "symbol",
          source: "parcels",
          layout: {
            "text-field": ["get", "parcel_number"],
            "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],

            "text-size": 12,
          },
          paint: {
            "text-color": "#ffffff",
            "text-halo-color": "#000000",
            "text-halo-width": 0,
          },
          minzoom: 16,
        });
      }

      // Add price display layer (initially hidden)
      if (!mapRef.current.getLayer("parcels-prices")) {
        mapRef.current.addLayer({
          id: "parcels-prices",
          type: "symbol",
          source: "parcels",
          layout: {
            "text-field": [
              "format",
              ["get", "parcel_number"],
              { "font-scale": 1.4, "text-color": "#ffffff" },

              "\n",
              {},
              ["to-string", ["get", "price_with_tax"]],
              { "font-scale": 1.0, "text-color": "#626262" },
              " SAR",
              { "font-scale": 1.0, "text-color": "#626262" },
            ],
            "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
            "text-size": 12,
            // "text-weight": "bold",

            "text-offset": [0, 0],
            "text-anchor": "center",
            "text-allow-overlap": false,
            "text-ignore-placement": false,
            visibility: "none", // Initially hidden
          },
          paint: {
            "text-color": "#ffffff",
            "text-halo-color": "#000000",
            "text-halo-width": 0,
          },
          minzoom: 14,
        });
      }

      // mapRef.current.setFilter('myLayer', ['==', ['get', 'floor_id'], 'layerId']);

      // Apply filters to layers
      const layers = ["parcels-fill", "parcels-outline", "parcels-labels"];

      layers.forEach((layerId) => {
        mapRef.current.setFilter(layerId, ["==", ["get", "floor_id"], floorId]);

        // console.log("DDDDDDD  setFilter done");
        // if (mapRef.current.getLayer(layerId)) {

        //   // if (filterExpression.length > 1) {
        //   //   mapRef.current.setFilter(layerId, filterExpression);
        //   // } else {
        //   //   mapRef.current.setFilter(layerId, null);
        //   // }
        // }
      });

      MapLogger.logLayerOperation("parcel-layers", "Added successfully");
    } catch (error) {
      MapErrorHandler.handleLayerError(error, "parcel-layers");
    }
  }

  /**
   * Add parcel layers to the map
   * @param {Object} mapRef - Map reference
   * @param {Object|string} parcelData - Parcel data or direct URL
   */
  static async addParcelLayers(mapRef, parcelData) {
    try {
      // Add data source
      if (typeof parcelData === "string") {
        // Direct URL
        mapRef.current.addSource("parcels", {
          type: "geojson",
          data: parcelData,
        });
      } else {
        // Direct data
        mapRef.current.addSource("parcels", {
          type: "geojson",
          data: parcelData,
        });
      }

      // Add parcels fill layer
      if (!mapRef.current.getLayer("parcels-fill")) {
        mapRef.current.addLayer({
          id: "parcels-fill",
          type: "fill",
          source: "parcels",
          paint: {
            "fill-color": [
              "case",
              ["==", ["get", "sales_status"], "Available"],
              "#10b981", // Emerald green
              ["==", ["get", "sales_status"], "Not Available"],
              "#3b82f6", // Blue
              ["==", ["get", "sales_status"], "Reserved"],
              "#f59e0b", // Amber
              ["==", ["get", "sales_status"], "Sold"],
              "#ef4444", // Red
              "#6b7280", // Gray
            ],
            "fill-opacity": 0.5,
          },
          minzoom: 14,
        });
      }

      // Add parcels outline layer
      if (!mapRef.current.getLayer("parcels-outline")) {
        mapRef.current.addLayer({
          id: "parcels-outline",
          type: "line",
          source: "parcels",
          paint: {
            "line-color": "#2980b9",
            "line-width": 1,
          },
          minzoom: 14,
        });
      }

      // Add parcels labels layer
      if (!mapRef.current.getLayer("parcels-labels")) {
        mapRef.current.addLayer({
          id: "parcels-labels",
          type: "symbol",
          source: "parcels",
          layout: {
            "text-field": ["get", "parcel_number"],
            "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],

            "text-size": 12,
          },
          paint: {
            "text-color": "#ffffff",
            "text-halo-color": "#000000",
            "text-halo-width": 0,
          },
          minzoom: 16,
        });
      }

      // Add price display layer (initially hidden)
      if (!mapRef.current.getLayer("parcels-prices")) {
        mapRef.current.addLayer({
          id: "parcels-prices",
          type: "symbol",
          source: "parcels",
          layout: {
            "text-field": [
              "format",
              ["get", "parcel_number"],
              { "font-scale": 1.4, "text-color": "#ffffff" },

              "\n",
              {},
              ["to-string", ["get", "price_with_tax"]],
              { "font-scale": 1.0, "text-color": "#626262" },
              " SAR",
              { "font-scale": 1.0, "text-color": "#626262" },
            ],
            "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
            "text-size": 12,
            // "text-weight": "bold",

            "text-offset": [0, 0],
            "text-anchor": "center",
            "text-allow-overlap": false,
            "text-ignore-placement": false,
            visibility: "none", // Initially hidden
          },
          paint: {
            "text-color": "#ffffff",
            "text-halo-color": "#000000",
            "text-halo-width": 0,
          },
          minzoom: 14,
        });
      }

      // // Add fill layer
      // mapRef.current.addLayer({
      //   id: "parcels-fill",
      //   type: "fill",
      //   source: "parcels",
      //   paint: {
      //     "fill-color": [
      //       "case",
      //       ["==", ["get", "sales_status"], "Available"], "#4CAF50",
      //       ["==", ["get", "sales_status"], "Reserved"], "#FF9800",
      //       ["==", ["get", "sales_status"], "Sold"], "#F44336",
      //       ["==", ["get", "sales_status"], "Not Available"], "#9E9E9E",
      //       "#E0E0E0"
      //     ],
      //     "fill-opacity": 0.7
      //   }
      // });

      // // Add outline layer
      // mapRef.current.addLayer({
      //   id: "parcels-outline",
      //   type: "line",
      //   source: "parcels",
      //   paint: {
      //     "line-color": "#ffffff",
      //     "line-width": 1
      //   }
      // });

      // // Add labels layer
      // mapRef.current.addLayer({
      //   id: "parcels-labels",
      //   type: "symbol",
      //   source: "parcels",
      //   layout: {
      //     "text-field": ["get", "parcel_number"],
      //     "text-font": ["Open Sans Regular", "Arial Unicode MS Regular"],
      //     "text-size": 12,
      //     "text-allow-overlap": true,
      //     "text-ignore-placement": false
      //   },
      //   paint: {
      //     "text-color": "#ffffff",
      //     "text-halo-color": "#000000",
      //     "text-halo-width": 1
      //   }
      // });

      MapLogger.logLayerOperation("parcel-layers", "Added successfully");
    } catch (error) {
      MapErrorHandler.handleLayerError(error, "parcel-layers");
    }
  }

  /**
   * Add floor basemap
   * @param {Object} mapRef - Map reference
   * @param {number} floorId - Floor ID
   */
  static async addFloorBasemap(mapRef, floorId) {
    try {
      const response = await getFloorBasemap(floorId);
      const basemapData = response.data.data;

      // 3d basemap
      if (
        basemapData.basemap_image3D_url &&
        basemapData.basemap_coordinates3d
      ) {
        try {
          // Remove existing source if it exists
          if (mapRef.current.getSource("floor-basemap-3d")) {
            mapRef.current.removeSource("floor-basemap-3d");
          }
        } catch (error) {
          // console.log('error removing source', error);
        }

        let coords3d = basemapData.basemap_coordinates3d;
        if (typeof coords3d === 'string') {
          try { coords3d = JSON.parse(coords3d); } catch (e) {}
        }

        if (Array.isArray(coords3d) && coords3d.length === 4) {
          mapRef.current.addSource("floor-basemap-3d", {
            type: "image",
            url: basemapData.basemap_image3D_url,
            coordinates: coords3d,
          });
          mapRef.current.addLayer({
            id: "floor-basemap-layer-3d",
            type: "raster",
            source: "floor-basemap-3d",
            paint: {
              "raster-opacity": 0.95,
            },
          });
        }
      }

      if (
        basemapData &&
        basemapData.has_basemap &&
        basemapData.basemap_image_url &&
        basemapData.basemap_coordinates
      ) {
        // Add basemap as image layer
        //check if  added remove it

        try {
          // Remove existing source if it exists
          if (mapRef.current.getSource("floor-basemap")) {
            mapRef.current.removeSource("floor-basemap");
          }
        } catch (error) {
          // console.log('error removing source', error);
        }

        let coords = basemapData.basemap_coordinates;
        if (typeof coords === 'string') {
          try { coords = JSON.parse(coords); } catch (e) {}
        }

        if (Array.isArray(coords) && coords.length === 4) {
          mapRef.current.addSource("floor-basemap", {
            type: "image",
            url: basemapData.basemap_image_url,
            coordinates: coords,
          });

          mapRef.current.addLayer({
            id: "floor-basemap-layer",
            type: "raster",
            source: "floor-basemap",
            paint: {
              "raster-opacity": 0.95,
            },
          });

          MapLogger.logLayerOperation("floor-basemap", "Added successfully");
        }
      }
    } catch (error) {
      MapErrorHandler.handleLayerError(error, "floor-basemap");
    }
  }

  static async addFloorBasemapProject(mapRef, project, floorId) {
    // console.log("####addFloorBasemapProject", project, floorId);
    const basemapData = (
      project.base_map_floors ??
      project.floors ??
      []
    ).filter((f) => f.id === floorId)[0];
    // console.log("####addFloorBasemapProject Select ", basemapData);
    try {
      // project.base_map_floors // loop and find floorId

      // const response = await getFloorBasemap(floorId);
      // const basemapData = base_map_floors.data;

      // 3d basemap
      if (
        basemapData.basemap_image3D_url &&
        basemapData.basemap_coordinates3d
      ) {
        try{
          if (mapRef.current.getLayer("floor-basemap-layer-3d")) {
          await mapRef.current.removeLayer("floor-basemap-layer-3d");
        }
        }catch(e){}

        try {
          // Remove existing source if it exists
          if (mapRef.current.getSource("floor-basemap-3d")) {
            mapRef.current.removeSource("floor-basemap-3d");
          }
        } catch (error) {
          // console.log('error removing source', error);
        }
        try {
          let coords3d = basemapData.basemap_coordinates3d;
          if (typeof coords3d === 'string') {
            try { coords3d = JSON.parse(coords3d); } catch (e) {}
          }
          if (Array.isArray(coords3d) && coords3d.length === 4) {
            mapRef.current.addSource("floor-basemap-3d", {
              type: "image",
              url: basemapData.basemap_image3D_url,
              coordinates: coords3d,
            });
            mapRef.current.addLayer({
              id: "floor-basemap-layer-3d",
              type: "raster",
              source: "floor-basemap-3d",
              paint: {
                "raster-opacity": 0.95,
              },
            });
          }
        } catch (error) {
          // console.log('error removing source', error);
        }
      }

      if (
        basemapData &&
        // basemapData.has_basemap &&
        basemapData.basemap_image_url &&
        basemapData.basemap_coordinates
      ) {
        // Add basemap as image layer
        //check if  added remove it
        try{
          if (mapRef.current.getLayer("floor-basemap-layer")) {
          await mapRef.current.removeLayer("floor-basemap-layer");
        }
        }catch(e){}

        try {
          // Remove existing source if it exists
          if (mapRef.current.getSource("floor-basemap")) {
            mapRef.current.removeSource("floor-basemap");
          }
        } catch (error) {
          // console.log('error removing source', error);
        }
        try {
          let coords = basemapData.basemap_coordinates;
          if (typeof coords === 'string') {
            try { coords = JSON.parse(coords); } catch (e) {}
          }
          if (Array.isArray(coords) && coords.length === 4) {
            mapRef.current.addSource("floor-basemap", {
              type: "image",
              url: basemapData.basemap_image_url,
              coordinates: coords,
            });

            mapRef.current.addLayer({
              id: "floor-basemap-layer",
              type: "raster",
              source: "floor-basemap",
              paint: {
                "raster-opacity": 0.95,
              },
            });
          }
        } catch (error) {
          // console.log('error removing source', error);
        }
        MapLogger.logLayerOperation("floor-basemap", "Added successfully");
      }
    } catch (error) {
      MapErrorHandler.handleLayerError(error, "floor-basemap");
    }
  }
  /**
   * Clear existing layers
   * @param {Object} mapRef - Map reference
   */
  static async clearExistingLayers(mapRef) {
    try {
      const layersToRemove = [
        "parcels-fill",
        "parcels-outline",
        "parcels-labels",
        "parcels-prices",
        "floor-basemap-layer",
        "floor-basemap-layer-3d",
        "parcel-dimensions",
        "parcel-dimension-lines",
      ];

      // Remove layers
      layersToRemove.forEach((layerId) => {
        if (mapRef.current.getLayer(layerId)) {
          mapRef.current.removeLayer(layerId);
        }
      });

      // Remove sources
      const sourcesToRemove = [
        "parcels",
        "floor-basemap",
        "floor-basemap-3d",
        "parcel-dimensions",
        "parcel-dimension-lines",
      ];
      sourcesToRemove.forEach((sourceId) => {
        if (mapRef.current.getSource(sourceId)) {
          mapRef.current.removeSource(sourceId);
        }
      });

      MapLogger.logLayerOperation("existing-layers", "Cleared successfully");
    } catch (error) {
      MapErrorHandler.handleLayerError(error, "clear-layers");
    }
  }

  /**
   * Apply filters to layers
   * @param {Object} mapRef - Map reference
   * @param {Object} filters - Applied filters
   * @param {string} projectType - Project type
   * @param {Array} currentFloorFilters - Current floor filters for residential projects
   */
  static applyFilters(
    mapRef,
    filters,
    projectType,
    currentFloorFilters = null
  ) {
    try {
      if (!mapRef.current || !mapRef.current.isStyleLoaded()) {
        return;
      }

      let filterExpression = ["all"];

      // console.log("####applyFilters", filters);

      // Floor filter for residential projects - maintain floor selection during zoom/pan
      if (
        projectType === "residential" &&
        currentFloorFilters &&
        currentFloorFilters.length > 0
      ) {
        const floorId = currentFloorFilters[0];
        filterExpression.push(["==", ["get", "floor_id"], floorId]);
        MapLogger.logMapInfo("Floor filter applied", { floorId, projectType });
      }

      // Sales status filter
      if (filters.salesStatus && filters.salesStatus.length > 0) {
        filterExpression.push([
          "in",
          ["get", "sales_status"],
          ["literal", filters.salesStatus],
        ]);
      }

      // Price range filter
      if (filters.priceRange && filters.isPriceFilterActive) {
        filterExpression.push([
          "all",
          [">=", ["get", "price_with_tax"], filters.priceRange.min],
          ["<=", ["get", "price_with_tax"], filters.priceRange.max],
        ]);
      }

      // Services filter
      if (filters.services && filters.services.length > 0) {
        const serviceFilters = filters.services.map((service) => [
          "in",
          service,
          ["get", "services_available"],
        ]);
        filterExpression.push(["any", ...serviceFilters]);
      }

      // Apply filters to layers
      const layers = ["parcels-fill", "parcels-outline", "parcels-labels"];

      layers.forEach((layerId) => {
        if (mapRef.current.getLayer(layerId)) {
          if (filterExpression.length > 1) {
            mapRef.current.setFilter(layerId, filterExpression);
          } else {
            mapRef.current.setFilter(layerId, null);
          }
        }
      });

      MapLogger.logLayerOperation("filters", "Applied successfully");
    } catch (error) {
      MapErrorHandler.handleLayerError(error, "apply-filters");
    }
  }
}

export default MapProjectService;
